import { selectFeatureFlags } from 'core/userContext';
import { useQueryContext } from 'modules/billing/contexts/QueryContext';
import { useMetricInfo } from 'modules/billing/hooks/useMetricInfo';
import { useSelector } from 'react-redux';
import { AnalyticCard } from '../AnalyticCard';
import { AnalyticTileProps } from '../AnalyticTile';
import { ProjectBreakdownLink } from '../ProjectBreakdownLink';
import { MESSAGING_CARD_IDS, MESSAGING_CARD_TEXT, MESSAGING_METRIC_NAMES } from './constants';
import { MessagingAnalyticTable } from './MessagingAnalyticTable';

const {
  cardTitle,
  emailMetricName,
  smsMetricName,
  mmsMetricName,
  projectBreakdownUrl,
  pushMetricName,
  webPushMetricName,
  inAppMetricName,
  whatsAppMetricName,
} = MESSAGING_CARD_TEXT;

export const MessagingUsageCard = () => {
  const { isLoading } = useQueryContext();
  const { ExpandedChannelMetrics, WhatsAppMessageChannelGA } = useSelector(selectFeatureFlags);

  const [
    emailsSentInfo,
    smsSegmentsSentInfo,
    mmsAttachmentsSentInfo,
    pushNotificationsSentInfo,
    webPushNotificationsSentInfo,
    inAppNotificationsSentInfo,
    whatsAppSentInfo,
  ] = useMetricInfo(MESSAGING_METRIC_NAMES);

  const baseMetric = {
    metricValuePrefix: 'Sent this period',
    timePeriod: 'per year',
    totalMetricValuePrefix: 'Total usage',
  };

  const baseMetrics: AnalyticTileProps[] = [
    {
      ...baseMetric,
      limit: emailsSentInfo.annualLimit,
      metricName: emailMetricName,
      metricValue: emailsSentInfo.value,
      timePeriod: emailsSentInfo.annualLimit ? baseMetric.timePeriod : '',
      totalMetricValue: emailsSentInfo.totalValue,
    },
    {
      ...baseMetric,
      limit: smsSegmentsSentInfo.annualLimit,
      metricName: smsMetricName,
      metricValue: smsSegmentsSentInfo.value,
      timePeriod: smsSegmentsSentInfo.annualLimit ? baseMetric.timePeriod : '',
      totalMetricValue: smsSegmentsSentInfo.totalValue,
    },
    {
      ...baseMetric,
      limit: mmsAttachmentsSentInfo.annualLimit,
      metricName: mmsMetricName,
      metricValue: mmsAttachmentsSentInfo.value,
      timePeriod: mmsAttachmentsSentInfo.annualLimit ? baseMetric.timePeriod : '',
      totalMetricValue: mmsAttachmentsSentInfo.totalValue,
    },
    {
      ...baseMetric,
      limit: pushNotificationsSentInfo.annualLimit,
      metricName: pushMetricName,
      metricValue: pushNotificationsSentInfo.value,
      timePeriod: pushNotificationsSentInfo.annualLimit ? baseMetric.timePeriod : '',
      totalMetricValue: pushNotificationsSentInfo.totalValue,
    },
  ];

  const expandedChannelMetrics = [
    {
      ...baseMetric,
      limit: webPushNotificationsSentInfo.annualLimit,
      metricName: webPushMetricName,
      metricValue: webPushNotificationsSentInfo.value,
      timePeriod: webPushNotificationsSentInfo.annualLimit ? baseMetric.timePeriod : '',
      totalMetricValue: webPushNotificationsSentInfo.totalValue,
    },
    {
      ...baseMetric,
      limit: inAppNotificationsSentInfo.annualLimit,
      metricName: inAppMetricName,
      metricValue: inAppNotificationsSentInfo.value,
      timePeriod: inAppNotificationsSentInfo.annualLimit ? baseMetric.timePeriod : '',
      totalMetricValue: inAppNotificationsSentInfo.totalValue,
    },
  ];

  const whatsAppMetric = {
    ...baseMetric,
    limit: whatsAppSentInfo.annualLimit,
    metricName: whatsAppMetricName,
    metricValue: whatsAppSentInfo.value,
    timePeriod: whatsAppSentInfo.annualLimit ? baseMetric.timePeriod : '',
    totalMetricValue: whatsAppSentInfo.totalValue,
  };

  const metricsWithExpandedChannels = ExpandedChannelMetrics
    ? baseMetrics.concat(expandedChannelMetrics)
    : baseMetrics;

  const messagingMetrics = WhatsAppMessageChannelGA
    ? metricsWithExpandedChannels.concat(whatsAppMetric)
    : metricsWithExpandedChannels;

  return (
    <AnalyticCard
      CardAction={<ProjectBreakdownLink to={projectBreakdownUrl} />}
      data-test={MESSAGING_CARD_IDS.cardId}
      isLoading={isLoading}
      metrics={messagingMetrics}
      title={cardTitle}
    >
      <MessagingAnalyticTable />
    </AnalyticCard>
  );
};
